import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Container,
    IconButton,
    Paper,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    useTheme,
    CircularProgress,
    Snackbar,
    Alert,
    Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useNavigate, useParams } from 'react-router-dom';
import { requestsService, RightsRequestDetails, StatusHistoryEntry } from '../../services/requests-service';
import { formatFriendlyDate } from '../../utils/dateUtils';

// Status color mapping - theme-aware (same as Requests.tsx)
const getStatusColor = (status: string, isDark: boolean) => {
    if (isDark) {
        switch (status.toUpperCase()) {
            case 'PENDING':
                return { bg: '#3E3B00', color: '#FFD54F', border: '#FFC107' };
            case 'IN_PROGRESS':
                return { bg: '#003D47', color: '#4DD0E1', border: '#17A2B8' };
            case 'COMPLETED':
                return { bg: '#1B5E20', color: '#81C784', border: '#28A745' };
            case 'REJECTED':
                return { bg: '#4A1E1E', color: '#E57373', border: '#DC3545' };
            case 'CANCELLED':
                return { bg: '#2C2C2C', color: '#9E9E9E', border: '#6C757D' };
            default:
                return { bg: '#2C2C2C', color: '#9E9E9E', border: '#6C757D' };
        }
    } else {
        switch (status.toUpperCase()) {
            case 'PENDING':
                return { bg: '#FFF3CD', color: '#856404', border: '#FFC107' };
            case 'IN_PROGRESS':
                return { bg: '#D1ECF1', color: '#0C5460', border: '#17A2B8' };
            case 'COMPLETED':
                return { bg: '#D4EDDA', color: '#155724', border: '#28A745' };
            case 'REJECTED':
                return { bg: '#F8D7DA', color: '#721C24', border: '#DC3545' };
            case 'CANCELLED':
                return { bg: '#E2E3E5', color: '#383D41', border: '#6C757D' };
            default:
                return { bg: '#E2E3E5', color: '#383D41', border: '#6C757D' };
        }
    }
};

// Priority color mapping - theme-aware (same as Requests.tsx)
const getPriorityColor = (priority: string, isDark: boolean) => {
    if (isDark) {
        switch (priority.toUpperCase()) {
            case 'LOW':
                return { bg: '#1B5E20', color: '#81C784', border: '#28A745' };
            case 'NORMAL':
                return { bg: '#003D47', color: '#4DD0E1', border: '#17A2B8' };
            case 'HIGH':
                return { bg: '#3E3B00', color: '#FFD54F', border: '#FFC107' };
            case 'URGENT':
                return { bg: '#4A1E1E', color: '#E57373', border: '#DC3545' };
            default:
                return { bg: '#2C2C2C', color: '#9E9E9E', border: '#6C757D' };
        }
    } else {
        switch (priority.toUpperCase()) {
            case 'LOW':
                return { bg: '#D4EDDA', color: '#155724', border: '#28A745' };
            case 'NORMAL':
                return { bg: '#D1ECF1', color: '#0C5460', border: '#17A2B8' };
            case 'HIGH':
                return { bg: '#FFF3CD', color: '#856404', border: '#FFC107' };
            case 'URGENT':
                return { bg: '#F8D7DA', color: '#721C24', border: '#DC3545' };
            default:
                return { bg: '#E2E3E5', color: '#383D41', border: '#6C757D' };
        }
    }
};

const RequestDetails: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { requestId } = useParams<{ requestId: string }>();

    // State management
    const [request, setRequest] = useState<RightsRequestDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusHistory, setStatusHistory] = useState<StatusHistoryEntry[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Dialog states
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [priorityDialogOpen, setPriorityDialogOpen] = useState(false);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [notesDialogOpen, setNotesDialogOpen] = useState(false);

    // Form states
    const [statusForm, setStatusForm] = useState({ status: '', reason: '', notes: '' });
    const [priorityForm, setPriorityForm] = useState({ priority: '' });
    const [assignForm, setAssignForm] = useState({ assignedTo: '' });
    const [notesForm, setNotesForm] = useState({ notes: '' });

    // Snackbar state
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

    // Fetch status history
    const fetchStatusHistory = useCallback(async (id: number) => {
        try {
            setHistoryLoading(true);
            const response = await requestsService.getRequestStatusHistory(id);
            if (response.success && response.data) {
                setStatusHistory(response.data);
            }
        } catch (err) {
            console.error('Failed to fetch status history:', err);
            // Don't show error to user, just log it
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    // Fetch request data
    useEffect(() => {
        const fetchRequest = async () => {
            if (!requestId) return;

            try {
                setLoading(true);
                setError(null);
                const response = await requestsService.getRequestDetails(parseInt(requestId));
                if (response.success && response.data) {
                    setRequest(response.data);
                    setNotesForm({ notes: response.data.complianceNotes || '' });
                    // Fetch status history after request data is loaded
                    fetchStatusHistory(parseInt(requestId));
                } else {
                    setError(response.error || 'Failed to fetch request details');
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch request details');
            } finally {
                setLoading(false);
            }
        };

        fetchRequest();
    }, [requestId, fetchStatusHistory]);

    const handleBackClick = () => {
        navigate('/workbench/requests');
    };

    const handleStatusUpdate = async () => {
        if (!request) return;

        try {
            const response = await requestsService.updateRequestStatus(request.requestId, {
                status: statusForm.status as any,
                reason: statusForm.reason || undefined,
                notes: statusForm.notes || undefined
            });

            if (response.success) {
                setSnackbar({ open: true, message: 'Status updated successfully', severity: 'success' });
                setStatusDialogOpen(false);
                setStatusForm({ status: '', reason: '', notes: '' });
                // Refresh request data
                const refreshResponse = await requestsService.getRequestDetails(request.requestId);
                if (refreshResponse.success && refreshResponse.data) {
                    setRequest(refreshResponse.data);
                }
                // Refresh status history after status update
                await fetchStatusHistory(request.requestId);
            } else {
                setSnackbar({ open: true, message: response.error || 'Failed to update status', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to update status', severity: 'error' });
        }
    };

    const handlePriorityUpdate = async () => {
        if (!request) return;

        try {
            const response = await requestsService.updatePriority(request.requestId, {
                priority: priorityForm.priority as any
            });

            if (response.success) {
                setSnackbar({ open: true, message: 'Priority updated successfully', severity: 'success' });
                setPriorityDialogOpen(false);
                setPriorityForm({ priority: '' });
                // Refresh request data
                const refreshResponse = await requestsService.getRequestDetails(request.requestId);
                if (refreshResponse.success && refreshResponse.data) {
                    setRequest(refreshResponse.data);
                }
            } else {
                setSnackbar({ open: true, message: response.error || 'Failed to update priority', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to update priority', severity: 'error' });
        }
    };

    const handleAssign = async () => {
        if (!request) return;

        try {
            const response = await requestsService.assignRequest(request.requestId, {
                assignedTo: assignForm.assignedTo ? parseInt(assignForm.assignedTo) : null
            });

            if (response.success) {
                setSnackbar({ open: true, message: 'Assignment updated successfully', severity: 'success' });
                setAssignDialogOpen(false);
                setAssignForm({ assignedTo: '' });
                // Refresh request data
                const refreshResponse = await requestsService.getRequestDetails(request.requestId);
                if (refreshResponse.success && refreshResponse.data) {
                    setRequest(refreshResponse.data);
                }
            } else {
                setSnackbar({ open: true, message: response.error || 'Failed to update assignment', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to update assignment', severity: 'error' });
        }
    };

    const handleNotesUpdate = async () => {
        if (!request) return;

        try {
            const response = await requestsService.addNotes(request.requestId, {
                notes: notesForm.notes
            });

            if (response.success) {
                setSnackbar({ open: true, message: 'Notes updated successfully', severity: 'success' });
                setNotesDialogOpen(false);
                // Refresh request data
                const refreshResponse = await requestsService.getRequestDetails(request.requestId);
                if (refreshResponse.success && refreshResponse.data) {
                    setRequest(refreshResponse.data);
                }
            } else {
                setSnackbar({ open: true, message: response.error || 'Failed to update notes', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to update notes', severity: 'error' });
        }
    };

    const getRequestTypeLabel = (type: string) => {
        return type.replace(/_/g, ' ');
    };

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error || !request) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ py: 3 }}>
                    <Typography variant="h6" color="error" gutterBottom>
                        Error Loading Request Details
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        {error || 'Request not found'}
                    </Typography>
                    <Button variant="contained" onClick={handleBackClick} sx={{ mt: 2 }}>
                        Back to Requests
                    </Button>
                </Box>
            </Container>
        );
    }

    const isDark = theme.palette.mode === 'dark';
    const statusColors = getStatusColor(request.requestStatus, isDark);
    const priorityColors = getPriorityColor(request.priority, isDark);

    return (
        <Container maxWidth="xl">
            <Box sx={{ py: 3 }}>
                {/* Header with Back Button */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconButton
                        onClick={handleBackClick}
                        sx={{
                            mr: 2,
                            color: 'primary.main',
                            '&:hover': {
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText'
                            }
                        }}
                    >
                        <ArrowBackIcon sx={{ fontSize: 24 }} />
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'text.primary' }}>
                            Request Details
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Request ID: #{request.requestId}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Chip
                            label={request.requestStatus}
                            sx={{
                                bgcolor: statusColors.bg,
                                color: statusColors.color,
                                border: `1px solid ${statusColors.border}`,
                                fontWeight: 600
                            }}
                        />
                        <Chip
                            label={request.priority}
                            sx={{
                                bgcolor: priorityColors.bg,
                                color: priorityColors.color,
                                border: `1px solid ${priorityColors.border}`,
                                fontWeight: 600
                            }}
                        />
                    </Box>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => {
                            setStatusForm({ status: request.requestStatus, reason: '', notes: '' });
                            setStatusDialogOpen(true);
                        }}
                    >
                        Update Status
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => {
                            setPriorityForm({ priority: request.priority });
                            setPriorityDialogOpen(true);
                        }}
                    >
                        Update Priority
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<AssignmentIcon />}
                        onClick={() => {
                            setAssignForm({ assignedTo: request.assignedTo?.toString() || '' });
                            setAssignDialogOpen(true);
                        }}
                    >
                        {request.assignedTo ? 'Reassign' : 'Assign'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => {
                            setNotesForm({ notes: request.complianceNotes || '' });
                            setNotesDialogOpen(true);
                        }}
                    >
                        Edit Notes
                    </Button>
                </Box>

                {/* Request Information */}
                <Grid container spacing={3}>
                    {/* Left Column - Main Details */}
                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                Request Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Request Type</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                                        {getRequestTypeLabel(request.requestType)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Description</Typography>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        {request.description || 'N/A'}
                                    </Typography>
                                </Grid>
                                {request.reason && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Reason</Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {request.reason}
                                        </Typography>
                                    </Grid>
                                )}
                                {request.purposeName && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Purpose</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                                                {request.purposeName}
                                            </Typography>
                                        </Grid>
                                        {request.purposeKey && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="caption" color="text.secondary">Purpose Key</Typography>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {request.purposeKey}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Data Principal ID</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, fontFamily: 'monospace' }}>
                                        #{request.dpId}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">
                                        {request.dpIdentifierToken ? 'Identifier Token' : 'Test Identifier'}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 500, fontFamily: 'monospace', wordBreak: 'break-all' }}
                                    >
                                        {request.dpIdentifierToken || request.dpIdentifier || 'Unavailable'}
                                    </Typography>
                                    {request.dpIdentifierType && (
                                        <Typography variant="caption" color="text.secondary">
                                            {request.dpIdentifierType}
                                        </Typography>
                                    )}
                                </Grid>
                                {request.legalBasis && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Legal Basis</Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {request.legalBasis}
                                        </Typography>
                                    </Grid>
                                )}
                                {request.requestedData && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Requested Data</Typography>
                                        <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                            <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                                {JSON.stringify(request.requestedData, null, 2)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>

                        {/* Compliance Notes */}
                        {request.complianceNotes && (
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                    Compliance Notes
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {request.complianceNotes}
                                </Typography>
                            </Paper>
                        )}
                    </Grid>

                    {/* Right Column - Metadata */}
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                Assignment
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Assigned To
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                                {request.assignedToName || 'Unassigned'}
                            </Typography>
                        </Paper>

                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                Timeline
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">Created Date</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {formatFriendlyDate(request.createdDate)}
                                </Typography>
                                {request.createdByName && (
                                    <Typography variant="caption" color="text.secondary">
                                        by {request.createdByName}
                                    </Typography>
                                )}
                            </Box>
                            {request.modifiedDate && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Last Modified</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {formatFriendlyDate(request.modifiedDate)}
                                    </Typography>
                                    {request.modifiedByName && (
                                        <Typography variant="caption" color="text.secondary">
                                            by {request.modifiedByName}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {request.estimatedCompletionDate && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Estimated Completion</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {formatFriendlyDate(request.estimatedCompletionDate)}
                                    </Typography>
                                </Box>
                            )}
                            {request.actualCompletionDate && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Actual Completion</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {formatFriendlyDate(request.actualCompletionDate)}
                                    </Typography>
                                </Box>
                            )}
                        </Paper>

                        {/* Status History Section */}
                        <Paper sx={{ p: 3, mt: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                Status History
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {historyLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : statusHistory.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                    No status history available
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {statusHistory.map((entry, index) => {
                                        const oldStatusColors = entry.oldStatus ? getStatusColor(entry.oldStatus, isDark) : null;
                                        const newStatusColors = getStatusColor(entry.newStatus, isDark);
                                        return (
                                            <Box
                                                key={entry.statusHistoryId}
                                                sx={{
                                                    p: 2,
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    borderRadius: 1,
                                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        {entry.oldStatus ? (
                                                            <>
                                                                <Chip
                                                                    label={entry.oldStatus}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: oldStatusColors?.bg,
                                                                        color: oldStatusColors?.color,
                                                                        border: `1px solid ${oldStatusColors?.border}`,
                                                                        fontSize: '0.75rem'
                                                                    }}
                                                                />
                                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    →
                                                                </Typography>
                                                            </>
                                                        ) : null}
                                                        <Chip
                                                            label={entry.newStatus}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: newStatusColors.bg,
                                                                color: newStatusColors.color,
                                                                border: `1px solid ${newStatusColors.border}`,
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatFriendlyDate(entry.changedDate)}
                                                    </Typography>
                                                </Box>
                                                {entry.statusChangeReason && (
                                                    <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                                                        <strong>Reason:</strong> {entry.statusChangeReason}
                                                    </Typography>
                                                )}
                                                {entry.notes && (
                                                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                                                        <strong>Processing Notes:</strong> {entry.notes}
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" color="text.secondary">
                                                    Changed by: {entry.changedByName || 'System'}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                {/* Status Update Dialog */}
                <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Update Request Status</DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>New Status</InputLabel>
                                <Select
                                    value={statusForm.status}
                                    label="New Status"
                                    onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                                >
                                    <MenuItem value="PENDING">Pending</MenuItem>
                                    <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                                    <MenuItem value="COMPLETED">Completed</MenuItem>
                                    <MenuItem value="REJECTED">Rejected</MenuItem>
                                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                label="Status Change Reason"
                                multiline
                                rows={3}
                                value={statusForm.reason}
                                onChange={(e) => setStatusForm({ ...statusForm, reason: e.target.value })}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Processing Notes (Optional)"
                                multiline
                                rows={3}
                                value={statusForm.notes}
                                onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })}
                                placeholder="Optional notes for this status change"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleStatusUpdate} variant="contained" disabled={!statusForm.status}>
                            Update
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Priority Update Dialog */}
                <Dialog open={priorityDialogOpen} onClose={() => setPriorityDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Update Request Priority</DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel>Priority</InputLabel>
                                <Select
                                    value={priorityForm.priority}
                                    label="Priority"
                                    onChange={(e) => setPriorityForm({ priority: e.target.value })}
                                >
                                    <MenuItem value="LOW">Low</MenuItem>
                                    <MenuItem value="NORMAL">Normal</MenuItem>
                                    <MenuItem value="HIGH">High</MenuItem>
                                    <MenuItem value="URGENT">Urgent</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPriorityDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handlePriorityUpdate} variant="contained" disabled={!priorityForm.priority}>
                            Update
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Assign Dialog */}
                <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Assign Request</DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Note: User assignment functionality will be implemented in a future update.
                            </Typography>
                            <TextField
                                fullWidth
                                label="User ID"
                                value={assignForm.assignedTo}
                                onChange={(e) => setAssignForm({ assignedTo: e.target.value })}
                                placeholder="Enter user ID (or leave empty to unassign)"
                                type="number"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleAssign} variant="contained">
                            {assignForm.assignedTo ? 'Assign' : 'Unassign'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Notes Dialog */}
                <Dialog open={notesDialogOpen} onClose={() => setNotesDialogOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Edit Compliance Notes</DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                label="Compliance Notes"
                                multiline
                                rows={8}
                                value={notesForm.notes}
                                onChange={(e) => setNotesForm({ notes: e.target.value })}
                                placeholder="Enter compliance notes for this request..."
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setNotesDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleNotesUpdate} variant="contained">
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </Container>
    );
};

export default RequestDetails;

